/**
 * Hook for fetching user profile data using React Query.
 *
 * Provides automatic request deduplication - multiple components calling this hook
 * will share a single network request, avoiding cache lock contention.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';
import {
  getPersistedUserProfile,
  setPersistedUserProfile,
} from '@/lib/localStorage/persistedUserProfile';
import type {
  MembershipListResponse,
  MembershipPreference,
  ProfileData,
} from '@/lib/profile/types';

import { useUser } from '../allauth_auth/hooks.js';

export const USER_PROFILE_QUERY_KEY = ['userProfile'] as const;

interface UseUserProfileDataOptions {
  /**
   * Whether to enable the query. When false, the query will not run.
   * Use this to gate the query on authentication status.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook to fetch the current user's profile data.
 *
 * Uses React Query for caching, deduplication, and automatic refetching.
 * Profile data is cached for 5 minutes.
 *
 * @param options - Configuration options
 * @param options.enabled - Whether to enable the query (default: true)
 * @returns React Query result with profile data
 *
 * @example
 * // Get just user info for display
 * const { data } = useUserProfileData();
 * const userName = data?.user.first_name;
 *
 * @example
 * // Gate by authentication status to avoid 401s
 * const [, { isAuthenticated }] = useAuthStatus();
 * const { data } = useUserProfileData({ enabled: isAuthenticated });
 *
 * @example
 * // Get full profile data including api_keys, social_accounts
 * const { data, isLoading } = useUserProfileData();
 * const hasTotp = data?.user_has_valid_totp_device;
 */
export function useUserProfileData(options: UseUserProfileDataOptions = {}) {
  const { enabled = true } = options;
  // Use the auth context's user id (not "most recent across all users") so a
  // shared device cannot leak the previous user's profile via initialData.
  const authUser = useUser() as { id?: number } | null;
  const authUserId = authUser?.id;

  const result = useQuery({
    queryKey: USER_PROFILE_QUERY_KEY,
    queryFn: async (): Promise<ProfileData> => {
      const response = await fetchWithDefaults('/users/api/profile/data/');
      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.statusText}`);
      }
      const data = await response.json();
      if (!data?.user?.id) {
        throw new Error('Invalid profile data: missing user.id');
      }
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
    // Hydrate ONLY from the persisted entry that matches the currently
    // authenticated user. If auth hasn't resolved yet, return undefined
    // and accept the brief flicker — security beats fast paint here.
    initialData: () =>
      typeof authUserId === 'number'
        ? getPersistedUserProfile(authUserId)
        : undefined,
    initialDataUpdatedAt: () => 0,
  });

  const { data: profileData } = result;

  useEffect(() => {
    if (!profileData?.user?.id) {
      return;
    }
    setPersistedUserProfile(profileData.user.id, profileData);
  }, [profileData]);

  return result;
}

/**
 * Hook to invalidate and refetch user profile data.
 *
 * @example
 * const invalidateProfile = useInvalidateUserProfile();
 * await invalidateProfile(); // Refetches profile data
 */
export function useInvalidateUserProfile() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
}

/**
 * Hook to clear user profile data from cache (without refetching).
 *
 * Use this on logout to ensure stale profile data doesn't persist
 * across user sessions.
 *
 * @example
 * const clearProfile = useClearUserProfile();
 * clearProfile(); // Removes profile data from cache
 */
export function useClearUserProfile() {
  const queryClient = useQueryClient();
  return () => queryClient.removeQueries({ queryKey: USER_PROFILE_QUERY_KEY });
}

/**
 * Hook to update cached user profile data optimistically.
 *
 * @example
 * const setProfileData = useSetUserProfileData();
 * setProfileData(prev => ({ ...prev, user: { ...prev.user, first_name: 'New Name' } }));
 */
export function useSetUserProfileData() {
  const queryClient = useQueryClient();
  return (
    updater: (prev: ProfileData | undefined) => ProfileData | undefined
  ) => {
    queryClient.setQueryData<ProfileData>(USER_PROFILE_QUERY_KEY, updater);
  };
}

/**
 * Query key factory for membership preferences.
 * Parameterized by orgId and userId for org-scoped data isolation.
 */
export const MEMBERSHIP_PREFERENCES_QUERY_KEY = (
  orgId: number | null,
  userId: number | null
) => ['membershipPreferences', orgId, userId] as const;

/**
 * Hook to fetch membership notification preferences for current user in given org.
 *
 * Uses React Query for caching, deduplication, and automatic refetching.
 * Preferences are cached for 5 minutes.
 *
 * @param orgId - Organization ID (required for query)
 * @param userId - User ID (required for query)
 * @returns React Query result with membership preference data
 *
 * @example
 * const { data, isLoading, error } = useMembershipPreferences(orgId, userId);
 * const membershipId = data?.id;
 */
export function useMembershipPreferences(
  orgId: number | null,
  userId: number | null
) {
  return useQuery({
    queryKey: MEMBERSHIP_PREFERENCES_QUERY_KEY(orgId, userId),
    queryFn: async (): Promise<MembershipPreference | null> => {
      if (!orgId || !userId) {
        throw new Error('orgId and userId are required');
      }

      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/memberships/`
      );

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}`);
        const logger = await import('@/lib/logger').then(m => m.logger);
        logger.error('Error fetching membership preferences', error, {
          orgId,
          userId,
          status: response.status,
        });
        throw error;
      }

      const data: MembershipListResponse = await response.json();
      const membership = data.results?.find(m => m.user_id === userId);
      return membership ?? null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!(orgId && userId), // Gate query execution
  });
}

/**
 * Hook to update cached membership preference data optimistically.
 *
 * @param orgId - Organization ID for cache key
 * @param userId - User ID for cache key
 * @example
 * const setMembershipData = useSetMembershipPreferences(orgId, userId);
 * setMembershipData(prev => prev ? { ...prev, agent_guidance: 'new value' } : prev);
 */
export function useSetMembershipPreferences(
  orgId: number | null,
  userId: number | null
) {
  const queryClient = useQueryClient();
  return (
    updater: (
      prev: MembershipPreference | null | undefined
    ) => MembershipPreference | null | undefined
  ) => {
    queryClient.setQueryData<MembershipPreference | null>(
      MEMBERSHIP_PREFERENCES_QUERY_KEY(orgId, userId),
      updater
    );
  };
}
